import { h } from 'preact';
import { useMemo, useRef, useState } from 'preact/hooks';
import Image from 'utils/image';
import setAttributes from 'utils/set-attributes';
import ProductQuantity from './product-quantity';
import MaxOrderMessage from './max-order-message';

// Add this helper function at the top of the file, after the imports
const appendSuffixToImageUrl = (url, suffix) => {
  if (!url) return url;
  const [path, query] = url.split('?');
  const lastDotIndex = path.lastIndexOf('.');
  if (lastDotIndex === -1) return url; // No file extension found
  const newPath = `${path.slice(0, lastDotIndex)}${suffix}${path.slice(lastDotIndex)}`;
  return query ? `${newPath}?${query}` : newPath;
};

const Price = ({ currentVariant }) => {
  // Helper function to safely format money
  const safeFormatMoney = (value) => {
    // Ensure value is a valid number
    const numValue = Number(value);

    if (Number.isNaN(numValue) || !Number.isFinite(numValue)) {
      // console.error('Invalid price amount:', value);
      return 'N/A';
    }

    try {
      // Convert cents to dollars
      const dollarValue = numValue / 100;

      // Determine if we need to show decimal places
      const hasDecimal = dollarValue % 1 !== 0;

      // Format the price with or without decimal places
      const formattedPrice = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: hasDecimal ? 2 : 0,
        maximumFractionDigits: 2,
      }).format(dollarValue);
      return formattedPrice;
    } catch (error) {
      // console.error('Error formatting price:', error);
      return 'N/A';
    }
  };

  const hasCompareAtPrice = currentVariant.compare_at_price && parseFloat(currentVariant.compare_at_price) > 0;
  const discountPercentage = hasCompareAtPrice
    ? Math.round(((currentVariant.compare_at_price - currentVariant.price) / currentVariant.compare_at_price) * 100)
    : 0;

  return (
    <div className="flex items-center">
      {hasCompareAtPrice && <s className="text-sm mr-2">{safeFormatMoney(currentVariant.compare_at_price)}</s>}
      <span className={`text-sm ${hasCompareAtPrice ? 'text-red' : ''}`}>{safeFormatMoney(currentVariant.price)}</span>
      {hasCompareAtPrice && <span className="text-sm text-red ml-2">({discountPercentage}% off)</span>}
    </div>
  );
};

const AddToCartButton = ({ currentVariant, qty, product }) => {
  const handleAddToCart = (e) => {
    e.preventDefault();
    const attributes = setAttributes(product);

    const addToCartEvent = new CustomEvent('cart:add', {
      detail: {
        payload: {
          ...window.getPayload(currentVariant, qty),
          attributes,
        },
      },
    });
    document.dispatchEvent(addToCartEvent);
  };

  return (
    <button
      className="atc-button button--upsell h-[40px] text-sm"
      disabled={!currentVariant.available}
      onClick={(e) => {
        e.preventDefault();
        handleAddToCart(e);
      }}
    >
      ADD
    </button>
  );
};

const ProductUpsellForm = ({ data }) => {
  const { product, maxOrderQTY, description } = data;
  const atcRef = useRef(null);
  const [qty, setQty] = useState(1);
  const giftForm = useRef(null);
  const currentVariant = product.variants[0];
  const productImage = product.images[0];

  // Remove the unused 'price' variable

  const addToCartButton = useMemo(
    () => (
      <AddToCartButton
        currentVariant={currentVariant}
        qty={qty}
        product={product}
        maxOrderQTY={maxOrderQTY}
        giftForm={giftForm}
      />
    ),
    [currentVariant, product, qty, maxOrderQTY],
  );

  const productQuantity = useMemo(
    () => (
      <ProductQuantity
        quantity={qty}
        changeQuantity={(value) => {
          setQty(value);
        }}
        maxOrder={maxOrderQTY?.max_order_qty}
      />
    ),
    [qty, maxOrderQTY],
  );

  const maxOrderMessage = useMemo(
    () => (
      <MaxOrderMessage maxOrder={maxOrderQTY?.max_order_qty} errorMessage={maxOrderQTY?.error_message} quantity={qty} />
    ),
    [maxOrderQTY, qty],
  );

  return (
    <div className="flex flex-row items-center space-x-4 lg:min-w-[340px] bg-[#F9F6F1] mb-[8px] p-[12px]">
      <div className="flex-shrink-0">
        <a href={`/products/${product.handle}`}>
          <Image
            src={appendSuffixToImageUrl(productImage, '_240x')}
            classes="block object-contain w-[100px] h-[100px] lg:w-[120px] lg:h-[120px]"
            alt={`${product.title} image`}
          />
        </a>
      </div>

      <div className="product-upsell-content flex-grow flex flex-col space-y-2">
        <div className="product-upsell-content-col lg:min-w-[155px]">
          <a href={`/products/${product.handle}`}>
            <h3 className="font-medium leading-[20px] mb-0">{product.title}</h3>
          </a>
          {description && (
            <div>
              <p className="leading-[20px] text-custom-black">{description}</p>
            </div>
          )}
          <div className="mt-1">
            <Price currentVariant={currentVariant} />
          </div>
        </div>
        <div className="product-upsell-atc-col ml-0">
          <div className="mr-2 hidden">{productQuantity}</div>
          <div ref={atcRef} className="flex-grow atc-wrapper">
            {addToCartButton}
          </div>
          {maxOrderQTY && maxOrderMessage}
        </div>
      </div>
    </div>
  );
};

export default ProductUpsellForm;
